/**
 * 1-bit bitmaps for thermal printers. A bitmap is { width, height, data } where data is a
 * Uint8Array of packed rows, MSB first, 1 = black dot, each row ceil(width / 8) bytes —
 * exactly the layout GS v 0 expects.
 *
 * Image decoding is environment-specific (canvas in the browser, sharp on the server); callers
 * pass plain grayscale or RGBA pixels here, so this module stays dependency-free.
 */

export function emptyBitmap(width, height) {
  return { width, height, data: new Uint8Array(Math.ceil(width / 8) * height) };
}

export function setDot(bmp, x, y) {
  const rowBytes = Math.ceil(bmp.width / 8);
  bmp.data[y * rowBytes + (x >> 3)] |= 0x80 >> (x & 7);
}

export function getDot(bmp, x, y) {
  const rowBytes = Math.ceil(bmp.width / 8);
  return (bmp.data[y * rowBytes + (x >> 3)] & (0x80 >> (x & 7))) !== 0;
}

/** RGBA (canvas ImageData layout) → grayscale 0–255, transparent pixels count as white. */
export function rgbaToGray(rgba, width, height) {
  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
    const a = rgba[p + 3] / 255;
    const lum = 0.299 * rgba[p] + 0.587 * rgba[p + 1] + 0.114 * rgba[p + 2];
    gray[i] = Math.round(lum * a + 255 * (1 - a));
  }
  return gray;
}

/**
 * Grayscale → 1-bit. 'threshold' suits text and QR codes; 'floyd' (Floyd–Steinberg error
 * diffusion) suits logos and photos.
 */
export function toBitmap(gray, width, height, { method = 'floyd', threshold = 128 } = {}) {
  const bmp = emptyBitmap(width, height);
  if (method === 'threshold') {
    for (let y = 0; y < height; y++)
      for (let x = 0; x < width; x++) if (gray[y * width + x] < threshold) setDot(bmp, x, y);
    return bmp;
  }
  const buf = Float32Array.from(gray);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const old = buf[i];
      const v = old < threshold ? 0 : 255;
      if (v === 0) setDot(bmp, x, y);
      const err = old - v;
      if (x + 1 < width) buf[i + 1] += (err * 7) / 16;
      if (y + 1 < height) {
        if (x > 0) buf[i + width - 1] += (err * 3) / 16;
        buf[i + width] += (err * 5) / 16;
        if (x + 1 < width) buf[i + width + 1] += err / 16;
      }
    }
  }
  return bmp;
}

/** Nearest-neighbour scale of a grayscale buffer (keeps logos crisp at small sizes). */
export function scaleGray(gray, width, height, newWidth, newHeight) {
  const out = new Uint8ClampedArray(newWidth * newHeight);
  for (let y = 0; y < newHeight; y++) {
    const sy = Math.min(height - 1, Math.floor((y * height) / newHeight));
    for (let x = 0; x < newWidth; x++) {
      const sx = Math.min(width - 1, Math.floor((x * width) / newWidth));
      out[y * newWidth + x] = gray[sy * width + sx];
    }
  }
  return out;
}

/** Fit (width, height) inside (maxW, maxH) keeping the aspect ratio; never upscales. */
export function fitSize(width, height, maxW, maxH) {
  const k = Math.min(1, maxW / width, maxH / height);
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

/** Place a bitmap on a wider canvas (left/center/right) — printers have no image alignment for GS v 0. */
export function alignBitmap(bmp, totalWidth, align = 'center') {
  if (bmp.width >= totalWidth) return bmp;
  const offset =
    align === 'left' ? 0 : align === 'right' ? totalWidth - bmp.width : Math.floor((totalWidth - bmp.width) / 2);
  const out = emptyBitmap(totalWidth, bmp.height);
  for (let y = 0; y < bmp.height; y++)
    for (let x = 0; x < bmp.width; x++) if (getDot(bmp, x, y)) setDot(out, x + offset, y);
  return out;
}

/** QR module matrix ({ size, get(x, y) }) → bitmap with `scale` dots per module and a quiet zone. */
export function qrMatrixToBitmap(matrix, scale = 6, quiet = 2) {
  const dim = (matrix.size + quiet * 2) * scale;
  const bmp = emptyBitmap(dim, dim);
  for (let my = 0; my < matrix.size; my++) {
    for (let mx = 0; mx < matrix.size; mx++) {
      if (!matrix.get(mx, my)) continue;
      for (let dy = 0; dy < scale; dy++)
        for (let dx = 0; dx < scale; dx++) setDot(bmp, (mx + quiet) * scale + dx, (my + quiet) * scale + dy);
    }
  }
  return bmp;
}
